const layerCount = (layers, key) => Number(layers.find((layer) => layer.key === key)?.count || 0)

const layerStatus = (layers, key) => layers.find((layer) => layer.key === key)?.status

export const siteDecisionCards = ({ selectedRecord, intelligence, radiusMiles = 10 } = {}) => {
  if (!selectedRecord) return []

  const layers = Array.isArray(intelligence?.layers) ? intelligence.layers : []
  const decisionLens = Array.isArray(intelligence?.decisionLens) ? intelligence.decisionLens : []
  const cards = []

  const powerCount = layerCount(layers, 'transmission_lines_hifld') + layerCount(layers, 'electric_substations_hifld')
  const queueCount = layerCount(layers, 'interconnection_queue_county_summaries') + layerCount(layers, 'interconnection_queue_projects')
  cards.push({
    key: 'power',
    label: 'Power readiness',
    tone: powerCount > 0 ? 'green' : 'amber',
    summary: powerCount > 0 ? `${powerCount} nearby grid feature${powerCount === 1 ? '' : 's'} within ${radiusMiles} mi.` : 'No nearby transmission/substation context loaded yet.',
    nextAction: queueCount > 0 ? 'Check queue pressure and utility territory before outreach.' : 'Enable queue and utility layers to complete power context.'
  })

  const gasCount = layerCount(layers, 'natural_gas_pipelines_eia') + layerCount(layers, 'gas_compressor_stations_hifld')
  cards.push({
    key: 'gas',
    label: 'Gas bridge',
    tone: gasCount > 0 ? 'green' : 'slate',
    summary: gasCount > 0 ? `${gasCount} gas infrastructure signal${gasCount === 1 ? '' : 's'} nearby.` : 'No bridge-power gas signal loaded.',
    nextAction: gasCount > 0 ? 'Confirm operator, pressure, tap feasibility, and gas-generation interconnect path.' : 'Use the Gas bridge recipe when bridge power is in scope.'
  })

  const envCount = layerCount(layers, 'fema_flood_hazard_zones') + layerCount(layers, 'nwi_wetlands') + layerCount(layers, 'epa_echo_facilities')
  cards.push({
    key: 'risk',
    label: 'Constraint scan',
    tone: envCount > 0 ? 'amber' : 'green',
    summary: envCount > 0 ? `${envCount} environmental/risk feature${envCount === 1 ? '' : 's'} in range.` : 'No loaded environmental constraints in range.',
    nextAction: envCount > 0 ? 'Review feature slideouts and confirm constraint boundaries against the parcel.' : 'Run the Environmental risk recipe before diligence sign-off.'
  })

  decisionLens.slice(0, 2).forEach((item) => {
    cards.push({
      key: `lens-${item.key}`,
      label: item.label,
      tone: item.tone || 'slate',
      summary: item.summary,
      nextAction: item.nextAction
    })
  })

  return cards.slice(0, 5)
}

export const comparisonRows = (records = []) => records.map((record) => ({
  id: record.id,
  name: record.name,
  market: record.market || record.county || 'Unknown market',
  stage: record.stageLabel || record.stage || 'Unknown',
  acreage: Number(record.acreage || 0),
  health: record.health?.stateLabel || 'Unknown',
  overdue: Number(record.taskSummary?.overdue || 0),
  blocked: Number(record.diligenceSummary?.blocked || 0),
  path: record.path
}))

export const compareTone = (row) => {
  if (row.blocked > 0 || row.overdue > 0 || /blocked|overdue/i.test(row.health)) return 'red'
  if (/attention|incomplete|review/i.test(row.health)) return 'amber'

  return 'green'
}

export const featureActionHints = (feature) => {
  const key = feature?.layerKey
  if (!key) return []

  if (['electric_substations_hifld', 'electric_substations_cached'].includes(key)) {
    return ['Look up parcel ownership around the substation.', 'Confirm utility owner and available capacity.', 'Save a note if this is a candidate delivery node.']
  }
  if (['transmission_lines_hifld', 'transmission_lines_cached'].includes(key)) {
    return ['Check voltage tier and owner.', 'Look up nearby parcels for tap or switchyard options.', 'Confirm status before treating this as in-service capacity.']
  }
  if (key === 'electric_retail_service_territories_hifld') {
    return ['Open the utility directory for large-load posture.', 'Capture utility contacts before outreach.', 'Verify territory against parcel geometry.']
  }
  if (key.includes('gas') || key.includes('lng') || key.includes('pipelines')) {
    return ['Confirm operator and pressure/capacity.', 'Check compressor/tap proximity.', 'Use as bridge-power context, not a guaranteed supply commitment.']
  }
  if (key.includes('fema') || key.includes('wetlands') || key.includes('habitat') || key.includes('epa')) {
    return ['Confirm boundaries against parcel geometry.', 'Record mitigation or permitting follow-up.', 'Escalate before LOI if the constraint hits developable acreage.']
  }

  return ['Review source attributes.', 'Save a note if this affects site diligence.', 'Cross-check with parcel and utility context.']
}
